<!--
  AdminDashboard.svelte — the shared, configurable analytics dashboard.

  One or more tabs, each a column grid of widgets that can be rearranged by
  dragging, with a page-wide range override, page-wide filters and auto-refresh.

  The configuration is a single server-side setting, **shared by every admin**:
  a chart someone adds is a chart the team sees. That is deliberate — an
  operational dashboard whose panels differ per viewer is a dashboard nobody can
  refer to in a conversation.

  The whole visible tab loads in one batch request. A tab of twenty widgets is
  twenty to forty queries, and one request each arrives as a burst the browser
  serialises over its connection limit while every one re-authenticates.
-->
<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { Alert, Button, Checkbox, Helper, Input, Label, Modal, Select, Spinner, Textarea } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { EmptyState, PageHeader } from '../../components';
	import MetricPicker from './analytics/MetricPicker.svelte';
	import MetricWidget from './analytics/MetricWidget.svelte';
	import { t, t2 } from '../../i18n';
	import { toastError, toastSuccess } from '../../toast';
	import {
		getAnalyticsCatalog,
		getAnalyticsConfiguration,
		getAnalyticsDashboard,
		getAnalyticsFieldValues,
		runAnalyticsBatch,
		saveAnalyticsConfiguration,
		saveAnalyticsDashboard
	} from '../../AppNetwork';
	import type {
		AnalyticsBatchCohortResult,
		AnalyticsBatchResult,
		AnalyticsCatalog,
		AnalyticsFieldValue,
		DashboardConfig,
		DashboardTab,
		DashboardWidget,
		DashboardWidgetFilter
	} from '../../AppTypes';
	import { pageRequests, widgetDeclaresField } from '../../analytics/dashboardQueries';
	import { downloadFile } from '../../analytics/metricCharts';
	import { dashboardTemplates, type DashboardTemplate } from '../../analytics/dashboardTemplates';

	/** Operators that take no value. */
	const VALUELESS_OPERATORS = new Set(['isNull', 'notNull']);

	/** The page-wide range choices. */
	const RANGE_OPTIONS = [
		{ value: 0, label: 'Per widget' },
		{ value: 7, label: '7 days' },
		{ value: 30, label: '30 days' },
		{ value: 90, label: '90 days' },
		{ value: 180, label: '180 days' },
		{ value: 365, label: '1 year' }
	];

	/** The auto-refresh choices. */
	const REFRESH_OPTIONS = [
		{ value: 0, label: 'Off' },
		{ value: 15, label: '15s' },
		{ value: 30, label: '30s' },
		{ value: 60, label: '1m' },
		{ value: 300, label: '5m' },
		{ value: 900, label: '15m' }
	];

	/** Operators offered for a page-wide clause, in the order they are most often wanted. */
	const FILTER_OPERATORS = [
		{ value: 'eq', label: 'equals' },
		{ value: 'neq', label: 'not equals' },
		{ value: 'contains', label: 'contains' },
		{ value: 'in', label: 'is one of' },
		{ value: 'gt', label: 'greater than' },
		{ value: 'gte', label: 'greater or equal' },
		{ value: 'lt', label: 'less than' },
		{ value: 'lte', label: 'less or equal' },
		{ value: 'isNull', label: 'is empty' },
		{ value: 'notNull', label: 'is not empty' }
	];

	/** The threshold colours offered in the editor. */
	const THRESHOLD_COLORS = ['green', 'yellow', 'red'];

	/** An empty dashboard, used before the stored one arrives and by Reset. */
	function emptyConfig(): DashboardConfig {
		return {
			tabs: [{ id: 'default', name: 'Overview', columnCount: 3, widgets: [] }],
			refreshInterval: 0,
			rangeOverrideDays: 0,
			filters: []
		};
	}

	/** The metric catalogue, once loaded. */
	let catalog: AnalyticsCatalog | undefined = $state();
	/** The dashboard configuration. */
	let config: DashboardConfig = $state(emptyConfig());
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Why the initial load failed, if it did. */
	let loadError = $state('');

	/** Which tab is showing. */
	let activeTab = $state(0);
	/** Whether the layout is being edited. */
	let editing = $state(false);
	/** Whether a save is in flight. */
	let saving = $state(false);
	/** The widget being dragged, if any. */
	let dragIndex: number | null = $state(null);
	/** The tab being dragged, if any. */
	let tabDragIndex: number | null = $state(null);
	/** Bumped by the refresh timer to force a reload. */
	let refreshToken = $state(0);
	/** The auto-refresh timer. */
	let refreshTimer: ReturnType<typeof setInterval> | undefined;
	/** The hidden file input behind the Import button. */
	let importInput: HTMLInputElement | undefined = $state();

	/*
	 * The whole visible tab is fetched in one request; the widgets are pure renderers
	 * over these maps. A new Map identity is what tells them fresh data has arrived.
	 */
	let pageResults: Map<string, AnalyticsBatchResult> = $state(new Map());
	/** The page's cohort results, keyed the same way. */
	let pageCohorts: Map<string, AnalyticsBatchCohortResult> = $state(new Map());
	/** Whether the page's batch is in flight. */
	let pageLoading = $state(false);
	/** Guards against an older batch landing after a newer one and overwriting it. */
	let pageLoadSequence = 0;

	/** The tab currently showing. */
	let tab: DashboardTab | undefined = $derived(
		config.tabs[Math.min(activeTab, config.tabs.length - 1)] ?? config.tabs[0]
	);

	/**
	 * Normalizes a configuration into the shape this page edits.
	 *
	 * @param raw - The configuration as it arrived.
	 * @returns The normalized configuration.
	 */
	function normalize(raw: DashboardConfig): DashboardConfig {
		const tabs =
			Array.isArray(raw?.tabs) && raw.tabs.length > 0
				? raw.tabs.map((entry) => ({
						id: entry.id,
						name: entry.name,
						columnCount: entry.columnCount ?? 3,
						widgets: entry.widgets ?? []
					}))
				: emptyConfig().tabs;
		return {
			tabs,
			refreshInterval: raw?.refreshInterval ?? 0,
			rangeOverrideDays: raw?.rangeOverrideDays ?? 0,
			filters: raw?.filters ?? []
		};
	}

	onMount(async () => {
		try {
			const [loadedCatalog, loadedConfig] = await Promise.all([getAnalyticsCatalog(), getAnalyticsDashboard()]);
			catalog = loadedCatalog;
			config = normalize(loadedConfig);
		} catch (caught) {
			loadError = caught instanceof Error ? caught.message : String(caught);
		} finally {
			loading = false;
		}
	});

	onDestroy(() => clearInterval(refreshTimer));

	/** Fetches every query the visible tab needs, in one request. */
	async function loadPage(): Promise<void> {
		const widgets = tab?.widgets ?? [];
		const requests = pageRequests(widgets, catalog, config.rangeOverrideDays ?? 0, config.filters ?? []);

		/* Nothing to ask for: an empty tab, or only section headers. */
		if (requests.queries.length === 0 && requests.cohorts.length === 0) {
			pageResults = new Map();
			pageCohorts = new Map();
			pageLoading = false;
			return;
		}

		const sequence = ++pageLoadSequence;
		pageLoading = true;
		/* A failed batch leaves the previous data in place; a blip must not blank the page. */
		try {
			const response = await runAnalyticsBatch({ Queries: requests.queries, Cohorts: requests.cohorts });
			/* A slower earlier batch must not clobber the newer one that replaced it. */
			if (sequence !== pageLoadSequence) return;
			pageResults = new Map((response.Results ?? []).map((result) => [result.Key, result]));
			pageCohorts = new Map((response.Cohorts ?? []).map((result) => [result.Key, result]));
		} catch (caught) {
			/* Leave the widgets showing their previous data rather than blanking on a blip. */
			if (sequence === pageLoadSequence) toastError(caught instanceof Error ? caught.message : String(caught));
		} finally {
			if (sequence === pageLoadSequence) pageLoading = false;
		}
	}

	/**
	 * Everything about the page that decides what should be shown. The data reloads when
	 * this changes and not otherwise — a layout edit that moves a widget must not refetch.
	 */
	let pageKey = $derived(
		JSON.stringify([
			catalog != null,
			activeTab,
			(tab?.widgets ?? []).map((widget) => [
				widget.id,
				widget.viz,
				widget.metric,
				widget.metrics ?? [],
				widget.granularity,
				widget.rangeDays,
				widget.groupBy ?? [],
				widget.filters ?? [],
				widget.maxGroups,
				widget.compare,
				widget.sparkline
			]),
			config.rangeOverrideDays ?? 0,
			config.filters ?? [],
			refreshToken
		])
	);

	let lastPageKey = '';
	$effect(() => {
		if (catalog == null || pageKey === lastPageKey) return;
		lastPageKey = pageKey;
		loadPage();
	});

	/* Auto-refresh, paused while editing so a reload cannot land mid-drag. */
	$effect(() => {
		clearInterval(refreshTimer);
		const interval = config.refreshInterval ?? 0;
		if (!editing && interval > 0) {
			refreshTimer = setInterval(() => (refreshToken += 1), interval * 1000);
		}
	});

	// ── Tab and configuration mutation ─────────────────────────

	/**
	 * Applies a change to the active tab.
	 *
	 * @param updated - The fields to change.
	 */
	function setTab(updated: Partial<DashboardTab>): void {
		config = {
			...config,
			tabs: config.tabs.map((entry, index) => (index === activeTab ? { ...entry, ...updated } : entry))
		};
	}

	/**
	 * Replaces the active tab's widgets.
	 *
	 * @param widgets - The new widget list, in display order.
	 */
	function setWidgets(widgets: DashboardWidget[]): void {
		setTab({ widgets });
	}

	/** Appends a blank tab and switches to it. */
	function fnAddTab(): void {
		const tabs = [...config.tabs, { id: `tab-${Date.now()}`, name: t2('New tab'), columnCount: 3, widgets: [] }];
		config = { ...config, tabs };
		activeTab = tabs.length - 1;
	}

	/** Removes the active tab. The last one stays — a dashboard with no tab has nowhere to add. */
	function fnDeleteTab(): void {
		if (config.tabs.length <= 1) return;
		const tabs = config.tabs.filter((_unused, index) => index !== activeTab);
		config = { ...config, tabs };
		activeTab = Math.max(0, Math.min(activeTab, tabs.length - 1));
	}

	/**
	 * Moves the dragged tab to a new position, keeping the active page selected by identity.
	 *
	 * @param target - The position to drop at.
	 */
	function fnDropTab(target: number): void {
		if (tabDragIndex == null || tabDragIndex === target) {
			tabDragIndex = null;
			return;
		}
		const activeId = config.tabs[activeTab]?.id;
		const tabs = [...config.tabs];
		const [moved] = tabs.splice(tabDragIndex, 1);
		tabs.splice(target, 0, moved);
		config = { ...config, tabs };
		const nextActive = tabs.findIndex((entry) => entry.id === activeId);
		activeTab = nextActive >= 0 ? nextActive : Math.min(target, tabs.length - 1);
		tabDragIndex = null;
	}

	/**
	 * Moves the dragged widget to a new position.
	 *
	 * @param target - The position to drop at.
	 */
	function fnDrop(target: number): void {
		if (dragIndex == null || dragIndex === target || tab == null) {
			dragIndex = null;
			return;
		}
		const widgets = [...tab.widgets];
		const [moved] = widgets.splice(dragIndex, 1);
		widgets.splice(target, 0, moved);
		setWidgets(widgets);
		dragIndex = null;
	}

	/**
	 * Removes one widget.
	 *
	 * @param index - The widget to remove.
	 */
	function fnRemoveWidget(index: number): void {
		setWidgets((tab?.widgets ?? []).filter((_unused, position) => position !== index));
	}

	// ── Save, import, export, reset ────────────────────────────

	/** Persists the configuration. It applies for every admin once saved. */
	async function fnSave(): Promise<void> {
		saving = true;
		/* A rejected save leaves the editor open with the layout intact. */
		try {
			config = normalize(await saveAnalyticsDashboard(config));
			editing = false;
			toastSuccess(t2('Dashboard saved.'));
		} catch (caught) {
			toastError(caught instanceof Error ? caught.message : String(caught));
		} finally {
			saving = false;
		}
	}

	/** Downloads the configuration as JSON. */
	function fnExport(): void {
		downloadFile('dashboard.json', JSON.stringify(config, null, 2), 'application/json');
	}

	/** Opens the file picker behind the Import button. */
	function fnImportClick(): void {
		importInput?.click();
	}

	/**
	 * Loads a configuration from a JSON file. It is not saved until the admin says so.
	 *
	 * @param event - The file input's change event.
	 */
	async function fnImportFile(event: Event): Promise<void> {
		const input = event.currentTarget as HTMLInputElement;
		const file = input.files?.[0];
		/* The picker was dismissed without choosing anything. */
		if (file == null) return;
		/* Anything unparseable is reported rather than left as a half-applied layout. */
		try {
			config = normalize(JSON.parse(await file.text()));
			activeTab = 0;
			toastSuccess(t2('Configuration imported. Review and save to apply.'));
		} catch (caught) {
			toastError(t2('Could not import that file: {{error}}', { error: String(caught) }));
		} finally {
			/* Clear the input so re-picking the same file fires change again. */
			input.value = '';
		}
	}

	/** Whether the reset confirmation is open. */
	let resetOpen = $state(false);

	/** Replaces the configuration with an empty one. Not persisted until saved. */
	function fnReset(): void {
		config = emptyConfig();
		activeTab = 0;
		resetOpen = false;
	}

	// ── Templates ──────────────────────────────────────────────

	/** Whether the template picker is open. */
	let templatePickerOpen = $state(false);

	/**
	 * Appends a tab pre-filled from a template and switches to it.
	 *
	 * @param template - The template to add.
	 */
	function fnAddFromTemplate(template: DashboardTemplate): void {
		const stamp = Date.now();
		const widgets = template.widgets.map((widget, index) => ({
			...widget,
			id: `w-${stamp}-${index}-${Math.floor(Math.random() * 1e6)}`
		}));
		const tabs = [
			...config.tabs,
			{ id: `tab-${stamp}`, name: template.name, columnCount: template.columnCount, widgets }
		];
		config = { ...config, tabs };
		activeTab = tabs.length - 1;
		templatePickerOpen = false;
	}

	// ── Page-wide filters ──────────────────────────────────────

	/** Whether the page-filter editor is open. */
	let filtersOpen = $state(false);
	/** The clauses being edited, applied only on Apply. */
	let draftFilters: DashboardWidgetFilter[] = $state([]);
	/** Distinct values per field key, populating the autocomplete lists. */
	let filterValues: Record<string, AnalyticsFieldValue[]> = $state({});
	/** Debounce timers for the value typeahead, per field key. */
	const valueSearchTimers: Record<string, ReturnType<typeof setTimeout>> = {};

	/**
	 * The fields that can be filtered across the page, each with how many of the visible
	 * widgets it would actually narrow. A field no widget declares does nothing, so it is
	 * not offered at all.
	 */
	let dashboardFilterFields = $derived.by(() => {
		const widgets = (tab?.widgets ?? []).filter((widget) => widget.viz !== 'section' && widget.viz !== 'cohort');
		const counts: Record<string, { label: string; count: number }> = {};
		/* Score every field in the catalogue by how much of this page it would narrow. */
		for (const metric of catalog?.Metrics ?? []) {
			for (const field of metric.Fields) {
				const applies = widgets.filter((widget) => widgetDeclaresField(widget, catalog, field.Key)).length;
				/* A field no widget declares would do nothing, so it is not offered. */
				if (applies === 0) continue;
				const existing = counts[field.Key];
				if (existing == null || applies > existing.count) counts[field.Key] = { label: field.Label, count: applies };
			}
		}
		return Object.entries(counts)
			.map(([key, entry]) => ({ key, label: entry.label, count: entry.count }))
			.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
	});

	/**
	 * The metric to autocomplete a field's values from: any on the page that declares it.
	 *
	 * @param fieldKey - The field to autocomplete.
	 * @returns A metric key, or undefined when nothing on the page offers the field.
	 */
	function metricForField(fieldKey: string): string | undefined {
		for (const widget of tab?.widgets ?? []) {
			const keys =
				widget.viz === 'multi' || widget.viz === 'funnel' || widget.viz === 'waterfall'
					? (widget.metrics ?? [])
					: [widget.metric];
			for (const key of keys) {
				if (catalog?.Metrics.find((metric) => metric.Key === key)?.Fields.some((field) => field.Key === fieldKey)) {
					return key;
				}
			}
		}
		return undefined;
	}

	/**
	 * Loads the distinct values of a field for the autocomplete list.
	 *
	 * @param fieldKey - The field to enumerate.
	 * @param search   - What has been typed, to narrow the list server-side.
	 */
	async function fnLoadFieldValues(fieldKey: string, search = ''): Promise<void> {
		if (fieldKey == null || fieldKey.length === 0) return;
		const metricKey = metricForField(fieldKey);
		if (metricKey == null) return;
		filterValues = { ...filterValues, [fieldKey]: await getAnalyticsFieldValues(metricKey, fieldKey, search) };
	}

	/**
	 * Drives the debounced server-side typeahead as a filter value is typed.
	 *
	 * @param fieldKey - The field being filtered.
	 * @param event    - The input event.
	 */
	function fnOnFilterValueInput(fieldKey: string, event: Event): void {
		const search = (event.target as HTMLInputElement)?.value ?? '';
		clearTimeout(valueSearchTimers[fieldKey]);
		valueSearchTimers[fieldKey] = setTimeout(() => fnLoadFieldValues(fieldKey, search), 250);
	}

	/** Opens the page-filter editor on a copy of the live clauses. */
	function fnOpenFilters(): void {
		draftFilters = (config.filters ?? []).map((filter) => ({ ...filter }));
		filtersOpen = true;
		for (const filter of draftFilters) fnLoadFieldValues(filter.field);
	}

	/** Adds an empty page-wide clause on the most widely applicable field. */
	function fnAddDashboardFilter(): void {
		const field = dashboardFilterFields[0]?.key ?? '';
		draftFilters = [...draftFilters, { field, operator: 'eq', value: '' }];
		fnLoadFieldValues(field);
	}

	/**
	 * Removes one page-wide clause.
	 *
	 * @param index - The clause to remove.
	 */
	function fnRemoveDashboardFilter(index: number): void {
		draftFilters = draftFilters.filter((_unused, position) => position !== index);
	}

	/** Applies the edited page-wide clauses, which reloads the page. */
	function fnApplyFilters(): void {
		config = {
			...config,
			filters: draftFilters
				.filter((filter) => filter.field.length > 0)
				.map((filter) => ({
					field: filter.field,
					operator: filter.operator,
					/* An `in` clause is typed as a comma-separated list but sent as an array. */
					value: VALUELESS_OPERATORS.has(filter.operator)
						? undefined
						: filter.operator === 'in'
							? String(filter.value ?? '')
									.split(',')
									.map((part) => part.trim())
									.filter((part) => part.length > 0)
							: filter.value
				}))
		};
		filtersOpen = false;
	}

	// ── Collection settings ────────────────────────────────────

	/** Whether the collection settings modal is open. */
	let collectionOpen = $state(false);
	/** Whether the collection settings have been read back. */
	let collectionLoaded = $state(false);
	/** Whether a collection save is in flight. */
	let collectionSaving = $state(false);
	/** How long an event is retained, in days. */
	let retentionDays = $state(180);
	/** Whether every request is recorded. */
	let trackActions = $state(true);
	/** The oldest retained event, showing what the window has actually produced. */
	let oldestEventDate: string | null = $state(null);

	/** Opens the collection settings, reading them on demand rather than at page load. */
	async function fnOpenCollection(): Promise<void> {
		collectionOpen = true;
		/* A failed read leaves the modal in its loading state rather than showing defaults. */
		try {
			const response = await getAnalyticsConfiguration();
			retentionDays = response.Configuration.EventRetentionDays;
			trackActions = response.Configuration.TrackControllerActions;
			oldestEventDate = response.OldestEventDate;
			collectionLoaded = true;
		} catch (caught) {
			/* Leave the modal in its loading state rather than editing values never read back. */
			toastError(caught instanceof Error ? caught.message : String(caught));
		}
	}

	/** Saves the collection settings. */
	async function fnSaveCollection(): Promise<void> {
		collectionSaving = true;
		/* A rejected save keeps the modal open so the entered values are not lost. */
		try {
			const response = await saveAnalyticsConfiguration({
				EventRetentionDays: retentionDays,
				TrackControllerActions: trackActions
			});
			retentionDays = response.Configuration.EventRetentionDays;
			trackActions = response.Configuration.TrackControllerActions;
			oldestEventDate = response.OldestEventDate;
			collectionOpen = false;
			toastSuccess(t2('Data collection settings saved.'));
		} catch (caught) {
			/* Keep the modal open so the entered values are not lost. */
			toastError(caught instanceof Error ? caught.message : String(caught));
		} finally {
			collectionSaving = false;
		}
	}

	// ── Widget editor ──────────────────────────────────────────

	/** Whether the widget editor is open. */
	let editorOpen = $state(false);
	/** The widget being edited, or -1 for a new one. */
	let editorIndex = $state(-1);
	/** The editor's working copy of every widget field. */
	let editorTitle = $state('');
	let editorMetric = $state('');
	let editorViz = $state('chart');
	let editorChartType = $state('area');
	let editorMetrics: Record<string, boolean> = $state({});
	let editorSpan = $state(1);
	let editorRowSpan = $state(1);
	let editorGranularity = $state('day');
	let editorRangeDays = $state(90);
	let editorGroupBy: Record<string, boolean> = $state({});
	let editorMaxGroups = $state(50);
	let editorFilters: { field: string; operator: string; value?: unknown }[] = $state([]);
	let editorCompare = $state(false);
	let editorSparkline = $state(false);
	let editorTarget = $state(100);
	let editorThresholds: { value: number; color: string }[] = $state([]);
	let editorThresholdDir = $state('above');
	let editorAnnotations = $state('');
	let editorGoal = $state(0);

	/** The fields the metric being edited declares. */
	let editorFields = $derived(catalog?.Metrics.find((metric) => metric.Key === editorMetric)?.Fields ?? []);
	/** Whether the widget being edited is grouped. */
	let editorGrouped = $derived(Object.values(editorGroupBy).some((on) => on));
	/** The metric keys selected for a multi-metric widget. */
	let editorMetricsList = $derived(Object.keys(editorMetrics).filter((key) => editorMetrics[key]));
	/** Whether the widget being edited carries several metrics. */
	let editorIsMulti = $derived(editorViz === 'multi' || editorViz === 'funnel' || editorViz === 'waterfall');
	/** Whether Apply should be available. */
	let editorValid = $derived(
		editorViz === 'section' ||
			editorViz === 'cohort' ||
			(editorIsMulti ? editorMetricsList.length > 0 : editorMetric.length > 0)
	);

	/** Opens the editor for a new widget. */
	function fnOpenAdd(): void {
		editorIndex = -1;
		editorTitle = '';
		editorMetric = catalog?.Metrics[0]?.Key ?? '';
		editorViz = 'chart';
		editorChartType = 'area';
		editorMetrics = {};
		editorSpan = 1;
		editorRowSpan = 1;
		editorGranularity = 'day';
		editorRangeDays = 90;
		editorGroupBy = {};
		editorMaxGroups = 50;
		editorFilters = [];
		editorCompare = false;
		editorSparkline = false;
		editorTarget = 100;
		editorThresholds = [];
		editorThresholdDir = 'above';
		editorAnnotations = '';
		editorGoal = 0;
		editorOpen = true;
	}

	/**
	 * Opens the editor on an existing widget.
	 *
	 * @param index - The widget to edit.
	 */
	function fnOpenEdit(index: number): void {
		const widget = (tab?.widgets ?? [])[index];
		/* The widget went while the menu was open. */
		if (widget == null) return;
		editorIndex = index;
		editorTitle = widget.title;
		editorMetric = widget.metric;
		editorViz = widget.viz;
		editorChartType = widget.chartType ?? 'area';
		editorMetrics = Object.fromEntries((widget.metrics ?? []).map((key) => [key, true]));
		editorSpan = widget.span;
		editorRowSpan = widget.rowSpan ?? 1;
		editorGranularity = widget.granularity ?? 'day';
		editorRangeDays = widget.rangeDays ?? 90;
		editorGroupBy = Object.fromEntries((widget.groupBy ?? []).map((key) => [key, true]));
		editorMaxGroups = widget.maxGroups ?? 50;
		editorFilters = (widget.filters ?? []).map((filter) => ({
			field: filter.field,
			operator: filter.operator,
			/* An `in` clause is stored as an array but edited as a comma-separated list. */
			value: Array.isArray(filter.value) ? filter.value.join(', ') : filter.value
		}));
		/* Preload each clause's values so the typeahead has something before anything is typed. */
		for (const filter of editorFilters) fnLoadFieldValues(filter.field);
		editorCompare = widget.compare ?? false;
		editorSparkline = widget.sparkline ?? false;
		editorTarget = widget.target ?? 100;
		editorThresholds = (widget.thresholds ?? []).map((threshold) => ({ ...threshold }));
		editorThresholdDir = widget.thresholdDirection ?? 'above';
		editorAnnotations = (widget.annotations ?? []).map((entry) => `${entry.date}: ${entry.label}`).join('\n');
		editorGoal = widget.goal ?? 0;
		editorOpen = true;
	}

	/**
	 * Toggles one group-by dimension on the widget being edited.
	 *
	 * @param key - The field key to split by, or stop splitting by.
	 */
	function fnToggleEditorGroupBy(key: string): void {
		editorGroupBy = { ...editorGroupBy, [key]: editorGroupBy[key] !== true };
	}

	/** Adds an empty filter clause to the widget being edited. */
	function fnAddEditorFilter(): void {
		const field = editorFields[0]?.Key ?? '';
		editorFilters = [...editorFilters, { field, operator: 'eq', value: '' }];
		/* Preload so the typeahead offers something before anything is typed. */
		fnLoadFieldValues(field);
	}

	/**
	 * Removes one of the edited widget's filter clauses.
	 *
	 * @param index - The clause to remove.
	 */
	function fnRemoveEditorFilter(index: number): void {
		editorFilters = editorFilters.filter((_unused, position) => position !== index);
	}

	/** Adds a threshold to the widget being edited. */
	function fnAddThreshold(): void {
		editorThresholds = [...editorThresholds, { value: 0, color: 'red' }];
	}

	/**
	 * Removes one threshold.
	 *
	 * @param index - The threshold to remove.
	 */
	function fnRemoveThreshold(index: number): void {
		editorThresholds = editorThresholds.filter((_unused, position) => position !== index);
	}

	/**
	 * Parses the annotation textarea into markers.
	 *
	 * @param text - One `YYYY-MM-DD: label` per line.
	 * @returns The well-formed markers; a malformed line is dropped.
	 */
	function parseAnnotations(text: string): { date: string; label: string }[] {
		return text
			.split('\n')
			.map((line) => line.trim())
			.filter((line) => line.length > 0)
			.map((line) => {
				const match = line.match(/^(\d{4}-\d{2}-\d{2})\s*[:-]?\s*(.*)$/);
				return match != null ? { date: match[1], label: match[2] } : null;
			})
			.filter((annotation): annotation is { date: string; label: string } => annotation != null);
	}

	/** Builds the widget the editor describes and puts it on the tab. */
	function fnSaveWidget(): void {
		if (tab == null) return;
		const id = editorIndex >= 0 ? tab.widgets[editorIndex].id : `w-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
		const span = Math.min(Math.max(1, Math.floor(editorSpan) || 1), tab.columnCount);
		const rowSpan = Math.min(Math.max(1, Math.floor(editorRowSpan) || 1), 4);

		let widget: DashboardWidget;
		/* Each family of visualization carries a different set of fields. */
		if (editorViz === 'section') {
			widget = { id, title: editorTitle.trim(), metric: '', viz: 'section', span: tab.columnCount, rowSpan: 1 };
		} else if (editorViz === 'cohort') {
			widget = {
				id,
				title: editorTitle.trim().length > 0 ? editorTitle.trim() : t2('Cohort retention'),
				metric: '',
				viz: 'cohort',
				span,
				rowSpan,
				rangeDays: Math.max(30, Math.floor(editorRangeDays) || 365)
			};
		} else if (editorIsMulti) {
			widget = {
				id,
				title: editorTitle.trim().length > 0 ? editorTitle.trim() : t2('Metrics'),
				metric: editorMetricsList[0] ?? '',
				metrics: editorMetricsList,
				viz: editorViz,
				span,
				rowSpan,
				chartType: editorChartType,
				granularity: editorGranularity,
				rangeDays: Math.max(1, Math.floor(editorRangeDays) || 90),
				annotations: editorViz === 'multi' ? parseAnnotations(editorAnnotations) : undefined,
				goal: editorViz === 'multi' && editorGoal > 0 ? editorGoal : undefined
			};
		} else {
			const metric = catalog?.Metrics.find((entry) => entry.Key === editorMetric);
			const validFields = new Set((metric?.Fields ?? []).map((field) => field.Key));
			const thresholds = editorThresholds
				.filter((threshold) => Number.isFinite(Number(threshold.value)))
				.map((threshold) => ({ value: Number(threshold.value), color: threshold.color }));
			widget = {
				id,
				title: editorTitle.trim().length > 0 ? editorTitle.trim() : (metric?.Label ?? t2('Widget')),
				metric: editorMetric,
				viz: editorViz,
				span,
				rowSpan,
				chartType: editorViz === 'chart' ? editorChartType : undefined,
				granularity: editorGranularity,
				rangeDays: Math.max(1, Math.floor(editorRangeDays) || 90),
				groupBy: Object.keys(editorGroupBy).filter((key) => editorGroupBy[key] && validFields.has(key)),
				filters: editorFilters
					.filter((filter) => filter.field.length > 0 && validFields.has(filter.field) && filter.operator.length > 0)
					.map((filter) => ({
						field: filter.field,
						operator: filter.operator,
						/* `in` is typed as a list but the engine rejects a bare string. */
						value: VALUELESS_OPERATORS.has(filter.operator)
							? undefined
							: filter.operator === 'in'
								? String(filter.value ?? '')
										.split(',')
										.map((part) => part.trim())
										.filter((part) => part.length > 0)
								: (filter.value as string)
					})),
				maxGroups: Math.min(Math.max(1, Math.floor(editorMaxGroups) || 50), 1000),
				compare: editorViz === 'value' ? editorCompare : undefined,
				sparkline: editorViz === 'value' ? editorSparkline : undefined,
				target: editorViz === 'gauge' ? Number(editorTarget) || 0 : undefined,
				thresholds: thresholds.length > 0 ? thresholds : undefined,
				thresholdDirection: thresholds.length > 0 ? editorThresholdDir : undefined,
				annotations: editorViz === 'chart' ? parseAnnotations(editorAnnotations) : undefined,
				goal: editorViz === 'chart' && editorGoal > 0 ? editorGoal : undefined
			};
		}

		const widgets = [...tab.widgets];
		/* Replace in place when editing, append when adding. */
		if (editorIndex >= 0) widgets[editorIndex] = widget;
		else widgets.push(widget);
		setWidgets(widgets);
		editorOpen = false;
	}
</script>

<PageHeader
	title={$t('Dashboard')}
	description={$t('A shared analytics dashboard. Changes apply for every administrator once saved.')}
/>

<div class="mb-4 flex flex-wrap items-center justify-end gap-2">
	<div class="hidden items-center gap-1 sm:flex">
		<i class="fa-solid fa-calendar-days text-xs text-gray-400"></i>
		<Select
			class="!w-36"
			size="sm"
			value={config.rangeOverrideDays ?? 0}
			onchange={(event) =>
				(config = { ...config, rangeOverrideDays: parseInt((event.currentTarget as HTMLSelectElement).value) })}
		>
			{#each RANGE_OPTIONS as option (option.value)}
				<option value={option.value}>{$t(option.label)}</option>
			{/each}
		</Select>
	</div>
	<div class="hidden items-center gap-1 sm:flex">
		<i class="fa-solid fa-rotate text-xs text-gray-400"></i>
		<Select
			class="!w-28"
			size="sm"
			value={config.refreshInterval ?? 0}
			onchange={(event) =>
				(config = { ...config, refreshInterval: parseInt((event.currentTarget as HTMLSelectElement).value) })}
		>
			{#each REFRESH_OPTIONS as option (option.value)}
				<option value={option.value}>{$t(option.label)}</option>
			{/each}
		</Select>
	</div>

	<Button
		size="sm"
		color={(config.filters ?? []).length > 0 ? 'primary' : 'light'}
		onclick={fnOpenFilters}
		disabled={catalog == null}
	>
		<i class="fa-solid fa-filter mr-1 text-xs"></i>
		{#if (config.filters ?? []).length > 0}
			{t2('{{count}} filters', { count: (config.filters ?? []).length })}
		{:else}
			{$t('Filters')}
		{/if}
	</Button>

	{#if editing}
		<Button size="sm" color="light" onclick={fnOpenAdd} disabled={catalog == null}>
			<i class="fa-solid fa-plus mr-1 text-xs"></i>{$t('Add widget')}
		</Button>
		<Button size="sm" color="light" onclick={fnOpenCollection} title={$t('Data collection settings')}>
			<i class="fa-solid fa-gear text-xs"></i>
		</Button>
		<Button size="sm" color="green" onclick={fnSave} disabled={saving}>
			{#if saving}<Spinner size="4" class="mr-2" />{/if}
			{$t('Save')}
		</Button>
		<Button size="sm" color="light" onclick={() => (editing = false)}>{$t('Done')}</Button>
	{:else}
		<Button size="sm" onclick={() => (editing = true)} disabled={catalog == null}>
			<i class="fa-solid fa-pen mr-1 text-xs"></i>{$t('Edit')}
		</Button>
	{/if}
</div>

{#if loading}
	<EmptyState variant="loading" title={$t('Loading dashboard…')} />
{:else if loadError.length > 0}
	<EmptyState variant="error" title={$t('Could not load the dashboard.')} description={loadError} />
{:else}
	<!-- Tab bar -->
	<div class="mb-4 flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-gray-700">
		{#each config.tabs as entry, index (entry.id)}
			<button
				type="button"
				draggable={editing}
				ondragstart={() => (tabDragIndex = index)}
				ondragover={(event) => event.preventDefault()}
				ondrop={(event) => {
					event.preventDefault();
					fnDropTab(index);
				}}
				class="-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors {editing
					? 'cursor-move'
					: ''} {tabDragIndex === index ? 'opacity-50' : ''} {index === activeTab
					? 'border-primary-600 text-primary-600'
					: 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}"
				onclick={() => (activeTab = index)}
			>
				{#if editing}
					<i class="fa-solid fa-grip-vertical mr-1 text-[10px] text-gray-400"></i>
				{/if}
				{$t(entry.name)}
			</button>
		{/each}
		{#if editing}
			<button
				type="button"
				class="px-2 py-2 text-gray-400 hover:text-primary-600"
				title={$t('Add blank page')}
				aria-label={$t('Add blank page')}
				onclick={fnAddTab}
			>
				<i class="fa-solid fa-plus text-xs"></i>
			</button>
			<button
				type="button"
				class="inline-flex items-center gap-1 px-2 py-2 text-sm text-gray-400 hover:text-primary-600"
				title={$t('Add page from a template')}
				onclick={() => (templatePickerOpen = true)}
			>
				<i class="fa-solid fa-table-cells-large text-xs"></i>{$t('From template')}
			</button>
		{/if}
	</div>

	{#if editing}
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="flex items-center gap-2">
				<Label class="mb-0">{$t('Tab name')}</Label>
				<Input
					class="!w-40"
					value={tab?.name ?? ''}
					oninput={(event) => setTab({ name: (event.currentTarget as HTMLInputElement).value })}
				/>
			</div>
			<div class="flex items-center gap-2">
				<Label for="dash-columns" class="mb-0">{$t('Columns')}</Label>
				<Select
					id="dash-columns"
					class="!w-20"
					size="sm"
					value={tab?.columnCount ?? 3}
					onchange={(event) =>
						setTab({ columnCount: parseInt((event.currentTarget as HTMLSelectElement).value) })}
				>
					{#each [1, 2, 3, 4, 5, 6] as count (count)}
						<option value={count}>{count}</option>
					{/each}
				</Select>
			</div>
			{#if config.tabs.length > 1}
				<Button size="xs" color="red" onclick={fnDeleteTab}>
					<i class="fa-solid fa-trash mr-1 text-xs"></i>{$t('Delete tab')}
				</Button>
			{/if}
			<span class="grow"></span>
			<Button size="xs" color="light" onclick={fnExport}>
				<i class="fa-solid fa-download mr-1 text-xs"></i>{$t('Export')}
			</Button>
			<Button size="xs" color="light" onclick={fnImportClick}>
				<i class="fa-solid fa-upload mr-1 text-xs"></i>{$t('Import')}
			</Button>
			<Button size="xs" color="light" onclick={() => (resetOpen = true)}>
				<i class="fa-solid fa-trash mr-1 text-xs"></i>{$t('Reset')}
			</Button>
			<input
				type="file"
				accept="application/json"
				class="hidden"
				bind:this={importInput}
				onchange={fnImportFile}
			/>
			<Helper class="w-full">{$t('Drag cards to rearrange. Changes apply for everyone once saved.')}</Helper>
		</div>
	{/if}

	{#if tab == null || tab.widgets.length === 0}
		<EmptyState
			variant="empty"
			title={editing ? $t('Add a widget to get started.') : $t('No widgets yet.')}
			description={editing
				? $t('Pick a metric and a visualization, or start from a template.')
				: $t('Click Edit to build your dashboard.')}
		/>
	{:else}
		<div
			class="grid gap-4"
			role="list"
			style="grid-template-columns: repeat({tab.columnCount}, minmax(0, 1fr)); grid-auto-rows: minmax(0, auto)"
		>
			{#each tab.widgets as widget, index (widget.id)}
				{#if widget.viz === 'section'}
					<!-- A full-width heading grouping the widgets that follow it. -->
					<div
						role="listitem"
						style="grid-column: 1 / -1"
						draggable={editing}
						ondragstart={() => (dragIndex = index)}
						ondragover={(event) => event.preventDefault()}
						ondrop={(event) => {
							event.preventDefault();
							fnDrop(index);
						}}
						class={editing && dragIndex === index ? 'opacity-50' : ''}
					>
						<div
							class="flex items-center border-b border-gray-200 pb-1 pt-2 dark:border-gray-700 {editing
								? 'cursor-move'
								: ''}"
						>
							{#if editing}
								<i class="fa-solid fa-grip-vertical mr-2 text-xs text-gray-400"></i>
							{/if}
							<h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
								{$t(widget.title) || $t('Section')}
							</h3>
							<span class="grow"></span>
							{#if editing}
								<button
									type="button"
									class="mr-2 text-gray-400 hover:text-primary-600"
									aria-label={$t('Edit widget')}
									onclick={() => fnOpenEdit(index)}
								>
									<i class="fa-solid fa-pen text-xs"></i>
								</button>
								<button
									type="button"
									class="text-gray-400 hover:text-red-600"
									aria-label={$t('Remove widget')}
									onclick={() => fnRemoveWidget(index)}
								>
									<i class="fa-solid fa-trash text-xs"></i>
								</button>
							{/if}
						</div>
					</div>
				{:else}
					<div
						role="listitem"
						style="grid-column: span {Math.min(widget.span, tab.columnCount)} / span {Math.min(
							widget.span,
							tab.columnCount
						)}; grid-row: span {widget.rowSpan ?? 1} / span {widget.rowSpan ?? 1}"
						draggable={editing}
						ondragstart={() => (dragIndex = index)}
						ondragover={(event) => event.preventDefault()}
						ondrop={(event) => {
							event.preventDefault();
							fnDrop(index);
						}}
						class={editing && dragIndex === index ? 'opacity-50' : ''}
					>
						<AbstractCard class="h-full {editing ? 'cursor-move ring-1 ring-gray-300 dark:ring-gray-600' : ''}">
							{#if editing}
								<div class="mb-1 flex items-center">
									<i class="fa-solid fa-grip-vertical text-xs text-gray-400"></i>
									<span class="grow"></span>
									<button
										type="button"
										class="mr-2 text-gray-400 hover:text-primary-600"
										aria-label={$t('Edit widget')}
										onclick={() => fnOpenEdit(index)}
									>
										<i class="fa-solid fa-pen text-xs"></i>
									</button>
									<button
										type="button"
										class="text-gray-400 hover:text-red-600"
										aria-label={$t('Remove widget')}
										onclick={() => fnRemoveWidget(index)}
									>
										<i class="fa-solid fa-trash text-xs"></i>
									</button>
								</div>
							{/if}
							<MetricWidget
								{widget}
								{catalog}
								rangeOverride={config.rangeOverrideDays ?? 0}
								results={pageResults}
								cohortResults={pageCohorts}
								{pageLoading}
								onretry={loadPage}
							/>
						</AbstractCard>
					</div>
				{/if}
			{/each}
		</div>
	{/if}
{/if}

<!-- Widget editor -->
<Modal bind:open={editorOpen} title={editorIndex >= 0 ? $t('Edit widget') : $t('Add widget')} size="md" outsideclose>
	{#if catalog != null}
		<div class="space-y-3">
			<div class="grid grid-cols-2 gap-3">
				<div>
					<Label for="w-viz" class="mb-1">{$t('Visualization')}</Label>
					<Select id="w-viz" bind:value={editorViz}>
						<option value="chart">{$t('Chart')}</option>
						<option value="multi">{$t('Multi-metric chart')}</option>
						<option value="funnel">{$t('Funnel')}</option>
						<option value="waterfall">{$t('Waterfall')}</option>
						<option value="value">{$t('Single value')}</option>
						<option value="table">{$t('Table (top N)')}</option>
						<option value="gauge">{$t('Gauge')}</option>
						<option value="cohort">{$t('Cohort retention')}</option>
						<option value="section">{$t('Section header')}</option>
					</Select>
				</div>
				<div>
					<Label for="w-title" class="mb-1">{$t('Title')}</Label>
					<Input
						id="w-title"
						bind:value={editorTitle}
						placeholder={editorViz === 'section' ? $t('Section heading') : $t('Defaults to the metric name')}
					/>
				</div>
			</div>

			{#if editorIsMulti}
				<div>
					<Label class="mb-1">{editorViz === 'multi' ? $t('Metrics') : $t('Stages (in order)')}</Label>
					<MetricPicker {catalog} multiple bind:selected={editorMetrics} scrollable />
					<Helper class="mt-1">
						{editorViz === 'funnel'
							? $t('Pick the funnel stages, widest first.')
							: editorViz === 'waterfall'
								? $t('Pick the movement components in order.')
								: $t('Pick two or more metrics to overlay as separate series.')}
					</Helper>
				</div>
			{:else if editorViz === 'cohort'}
				<Helper>
					{$t(
						'Cohort retention groups accounts by signup month and shows the share active each month after. The range sets the lookback window.'
					)}
				</Helper>
			{:else if editorViz !== 'section'}
				<div>
					<Label class="mb-1">{$t('Metric')}</Label>
					<MetricPicker {catalog} bind:value={editorMetric} scrollable />
				</div>
			{/if}

			<div class="grid grid-cols-3 gap-3">
				<div>
					<Label for="w-span" class="mb-1">{$t('Width')}</Label>
					<Input
						id="w-span"
						type="number"
						bind:value={editorSpan}
						min="1"
						max={tab?.columnCount ?? 6}
						disabled={editorViz === 'section'}
					/>
				</div>
				{#if editorViz !== 'section'}
					<div>
						<Label for="w-rowspan" class="mb-1">{$t('Height')}</Label>
						<Input id="w-rowspan" type="number" bind:value={editorRowSpan} min="1" max="4" />
					</div>
					<div>
						<Label for="w-range" class="mb-1">{$t('Range (days)')}</Label>
						<Input id="w-range" type="number" bind:value={editorRangeDays} min="1" />
					</div>
				{/if}
			</div>

			{#if editorViz === 'chart' || editorViz === 'multi'}
				<div class="grid grid-cols-2 gap-3">
					<div>
						<Label for="w-charttype" class="mb-1">{$t('Chart type')}</Label>
						<Select id="w-charttype" bind:value={editorChartType}>
							<option value="area">{$t('Area')}</option>
							<option value="line">{$t('Line')}</option>
							<option value="bar">{$t('Bar')}</option>
							<option value="pie">{$t('Pie')}</option>
							<option value="donut">{$t('Donut')}</option>
							<option value="radar">{$t('Radar')}</option>
							<option value="polarArea">{$t('Polar area')}</option>
						</Select>
					</div>
					<div>
						<Label for="w-gran" class="mb-1">{$t('Granularity')}</Label>
						<Select id="w-gran" bind:value={editorGranularity}>
							{#each catalog.Granularities as option (option)}
								<option value={option}>{$t(option)}</option>
							{/each}
						</Select>
					</div>
				</div>
				{#if editorChartType === 'pie' || editorChartType === 'donut' || editorChartType === 'radar' || editorChartType === 'polarArea'}
					<Helper>
						{$t('Pie, donut, radar and polar charts sum each group into one slice — pick a Group by below.')}
					</Helper>
				{/if}
			{/if}

			{#if editorViz === 'value'}
				<div class="flex flex-wrap gap-x-6 gap-y-1">
					<Checkbox bind:checked={editorCompare}
						><span class="ml-1">{$t('Show change vs. previous period')}</span></Checkbox
					>
					<Checkbox bind:checked={editorSparkline}><span class="ml-1">{$t('Show trend sparkline')}</span></Checkbox>
				</div>
			{/if}

			{#if editorViz === 'gauge'}
				<div>
					<Label for="w-target" class="mb-1">{$t('Target (100%)')}</Label>
					<Input id="w-target" type="number" bind:value={editorTarget} min="0" class="!w-40" />
				</div>
			{/if}

			{#if (editorViz === 'chart' || editorViz === 'table') && editorFields.length > 0}
				<div>
					<Label class="mb-1">{$t('Group by')}</Label>
					<div class="flex flex-wrap gap-x-4 gap-y-1">
						{#each editorFields as field (field.Key)}
							<!-- One-way plus a handler; see the note in AdminMetrics for why never `bind:`. -->
							<Checkbox
								checked={editorGroupBy[field.Key] === true}
								onchange={() => fnToggleEditorGroupBy(field.Key)}
							>
								<span class="ml-1">{$t(field.Label)}</span>
							</Checkbox>
						{/each}
					</div>
				</div>
				{#if editorGrouped}
					<div>
						<Label for="w-maxgroups" class="mb-1">{$t('Max groups')}</Label>
						<Input
							id="w-maxgroups"
							type="number"
							bind:value={editorMaxGroups}
							min="1"
							max={catalog.MaxGroups}
							class="!w-32"
						/>
					</div>
				{/if}
			{/if}

			{#if editorViz === 'value' || editorViz === 'gauge'}
				<div>
					<div class="mb-1 flex items-center">
						<Label class="mb-0">{$t('Thresholds')}</Label>
						<span class="grow"></span>
						<Button size="xs" color="light" onclick={fnAddThreshold}>
							<i class="fa-solid fa-plus mr-1 text-xs"></i>{$t('Add')}
						</Button>
					</div>
					{#if editorThresholds.length === 0}
						<Helper>{$t('No thresholds — the value uses the default colour.')}</Helper>
					{:else}
						<div class="mb-2 flex items-center gap-2">
							<Label class="mb-0 text-xs">{$t('Trip when value is')}</Label>
							<Select class="!w-32" size="sm" bind:value={editorThresholdDir}>
								<option value="above">{$t('at or above')}</option>
								<option value="below">{$t('at or below')}</option>
							</Select>
						</div>
					{/if}
					{#each editorThresholds as threshold, index (index)}
						<div class="mb-1 flex items-center gap-2">
							<Input class="!w-32" type="number" bind:value={threshold.value} placeholder={$t('Value')} />
							<Select class="!w-28" bind:value={threshold.color}>
								{#each THRESHOLD_COLORS as color (color)}
									<option value={color}>{$t(color)}</option>
								{/each}
							</Select>
							<Button
								size="xs"
								color="red"
								onclick={() => fnRemoveThreshold(index)}
								aria-label={$t('Remove threshold')}
							>
								<i class="fa-solid fa-trash text-xs"></i>
							</Button>
						</div>
					{/each}
				</div>
			{/if}

			{#if editorViz === 'chart' || editorViz === 'multi'}
				<div>
					<Label for="w-goal" class="mb-1">{$t('Goal line')}</Label>
					<Input id="w-goal" type="number" bind:value={editorGoal} min="0" class="!w-40" placeholder={$t('None')} />
					<Helper class="mt-1">{$t('Draws a horizontal target line. 0 = none.')}</Helper>
				</div>
				<div>
					<Label for="w-annot" class="mb-1">{$t('Annotations')}</Label>
					<Textarea id="w-annot" rows={2} bind:value={editorAnnotations} placeholder="2026-06-01: Release 2.0" />
					<Helper class="mt-1">{$t('One per line, as YYYY-MM-DD: label.')}</Helper>
				</div>
			{/if}

			{#if editorViz !== 'section' && editorViz !== 'cohort' && !editorIsMulti}
				<div>
					<div class="mb-1 flex items-center">
						<Label class="mb-0">{$t('Filters')}</Label>
						<span class="grow"></span>
						<Button size="xs" color="light" onclick={fnAddEditorFilter} disabled={editorFields.length === 0}>
							<i class="fa-solid fa-plus mr-1 text-xs"></i>{$t('Add')}
						</Button>
					</div>
					{#if editorFilters.length === 0}
						<Helper>{$t('No filters.')}</Helper>
					{/if}
					{#each Object.keys(filterValues) as fieldKey (fieldKey)}
						<datalist id={`wfv-${fieldKey}`}>
							{#each filterValues[fieldKey] as suggestion (suggestion.Value)}
								<option value={suggestion.Value}>{suggestion.Label ?? suggestion.Value}</option>
							{/each}
						</datalist>
					{/each}
					{#each editorFilters as filter, index (index)}
						<div class="mb-1 flex flex-wrap items-center gap-2">
							<Select class="!w-36" bind:value={filter.field} onchange={() => fnLoadFieldValues(filter.field)}>
								{#each editorFields as field (field.Key)}
									<option value={field.Key}>{$t(field.Label)}</option>
								{/each}
							</Select>
							<Select class="!w-28" bind:value={filter.operator}>
								{#each catalog.Operators as operator (operator)}
									<option value={operator}>{operator}</option>
								{/each}
							</Select>
							{#if !VALUELESS_OPERATORS.has(filter.operator)}
								<Input
									class="!w-40"
									bind:value={filter.value as string}
									list={`wfv-${filter.field}`}
									oninput={(event) => fnOnFilterValueInput(filter.field, event)}
									placeholder={filter.operator === 'in' ? $t('Comma-separated') : $t('Value')}
								/>
							{/if}
							<Button
								size="xs"
								color="red"
								onclick={() => fnRemoveEditorFilter(index)}
								aria-label={$t('Remove filter')}
							>
								<i class="fa-solid fa-trash text-xs"></i>
							</Button>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{/if}
	{#snippet footer()}
		<Button onclick={fnSaveWidget} disabled={!editorValid}>{$t('Apply')}</Button>
		<Button color="light" onclick={() => (editorOpen = false)}>{$t('Cancel')}</Button>
	{/snippet}
</Modal>

<!-- Template picker -->
<Modal bind:open={templatePickerOpen} title={$t('Add a page from a template')} size="lg" outsideclose>
	<p class="mb-3 text-sm text-gray-500">
		{$t('Each template adds a page pre-filled with curated widgets. Edit or remove anything afterwards.')}
	</p>
	<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
		{#each dashboardTemplates as template (template.id)}
			<button
				type="button"
				class="flex items-start gap-3 rounded-lg border border-gray-200 bg-depth bg-depth-hover p-3 text-left transition-colors hover:border-primary-500 dark:border-gray-700"
				onclick={() => fnAddFromTemplate(template)}
			>
				<div class="mt-0.5 shrink-0 text-primary-600">
					<i class="{template.icon} text-xl"></i>
				</div>
				<div class="min-w-0">
					<div class="text-sm font-semibold">{$t(template.name)}</div>
					<div class="text-xs text-gray-500 dark:text-gray-400">{$t(template.description)}</div>
					<div class="mt-1 text-xs text-gray-400">
						{t2('{{count}} widgets', {
							count: template.widgets.filter((widget) => widget.viz !== 'section').length
						})}
					</div>
				</div>
			</button>
		{/each}
	</div>
</Modal>

<!-- Page-wide filters -->
<Modal bind:open={filtersOpen} size="lg" autoclose={false} title={$t('Dashboard filters')}>
	<p class="text-sm text-gray-500">
		{$t(
			'These narrow every widget on this page. A filter is applied only to widgets whose metric offers that field — the rest are left unfiltered.'
		)}
	</p>

	{#if dashboardFilterFields.length === 0}
		<Helper>{$t('No filterable fields on this page yet. Add a widget first.')}</Helper>
	{:else}
		<div class="mt-3 space-y-2">
			{#each draftFilters as filter, index (index)}
				<div class="flex flex-wrap items-center gap-2">
					<Select class="!w-48" bind:value={filter.field} onchange={() => fnLoadFieldValues(filter.field)}>
						{#each dashboardFilterFields as field (field.key)}
							<option value={field.key}>{$t(field.label)} ({field.count})</option>
						{/each}
					</Select>
					<Select class="!w-40" bind:value={filter.operator}>
						{#each FILTER_OPERATORS as operator (operator.value)}
							<option value={operator.value}>{$t(operator.label)}</option>
						{/each}
					</Select>
					{#if !VALUELESS_OPERATORS.has(filter.operator)}
						<Input
							class="!w-56"
							bind:value={filter.value as string}
							list={`dfv-${filter.field}`}
							oninput={(event) => fnOnFilterValueInput(filter.field, event)}
							placeholder={filter.operator === 'in' ? $t('Comma-separated values') : $t('Value')}
						/>
					{/if}
					<Button
						size="xs"
						color="red"
						onclick={() => fnRemoveDashboardFilter(index)}
						aria-label={$t('Remove filter')}
					>
						<i class="fa-solid fa-trash text-xs"></i>
					</Button>
				</div>
			{:else}
				<Helper>{$t('No filters. Every widget shows all data in its range.')}</Helper>
			{/each}
		</div>

		{#each Object.keys(filterValues) as fieldKey (fieldKey)}
			<datalist id={`dfv-${fieldKey}`}>
				{#each filterValues[fieldKey] as suggestion (suggestion.Value)}
					<option value={suggestion.Value}>{suggestion.Label ?? suggestion.Value}</option>
				{/each}
			</datalist>
		{/each}

		<Button size="xs" color="light" class="mt-3" onclick={fnAddDashboardFilter}>
			<i class="fa-solid fa-plus mr-1 text-xs"></i>{$t('Add filter')}
		</Button>
	{/if}

	{#snippet footer()}
		<Button color="green" onclick={fnApplyFilters}>{$t('Apply')}</Button>
		<Button color="light" onclick={() => (filtersOpen = false)}>{$t('Cancel')}</Button>
	{/snippet}
</Modal>

<!-- Data collection settings -->
<Modal bind:open={collectionOpen} size="sm" autoclose={false} title={$t('Data collection')}>
	{#if !collectionLoaded}
		<div class="flex justify-center py-6"><Spinner size="6" /></div>
	{:else}
		<div class="space-y-4">
			<div>
				<Label for="analytics-retention" class="mb-1">{$t('Event retention (days)')}</Label>
				<Input id="analytics-retention" type="number" min="0" max="3650" bind:value={retentionDays} />
				<Helper class="mt-1">
					{$t('Events older than this are deleted hourly. Use 0 to keep them forever.')}
				</Helper>
			</div>
			<div>
				<Checkbox bind:checked={trackActions}>{$t('Record an event for every request')}</Checkbox>
				<Helper class="mt-1">
					{$t(
						'Turning this off stops new events being written. Metrics computed from accounts, sessions and applications keep working.'
					)}
				</Helper>
			</div>
			{#if oldestEventDate != null}
				<Helper>
					{t2('Oldest retained event: {{date}}', { date: new Date(oldestEventDate).toLocaleDateString() })}
				</Helper>
			{:else}
				<Helper>{$t('No events have been recorded yet.')}</Helper>
			{/if}
		</div>
	{/if}
	{#snippet footer()}
		<Button color="green" onclick={fnSaveCollection} disabled={collectionSaving || !collectionLoaded}>
			{#if collectionSaving}<Spinner size="4" class="mr-2" />{/if}
			{$t('Save')}
		</Button>
		<Button color="light" onclick={() => (collectionOpen = false)}>{$t('Cancel')}</Button>
	{/snippet}
</Modal>

<!-- Reset confirmation -->
<Modal bind:open={resetOpen} size="xs" autoclose={false} title={$t('Reset the dashboard?')}>
	<Alert color="red">
		{$t('Every tab and widget is removed. This cannot be undone once saved.')}
	</Alert>
	{#snippet footer()}
		<Button color="red" onclick={fnReset}>{$t('Reset')}</Button>
		<Button color="light" onclick={() => (resetOpen = false)}>{$t('Cancel')}</Button>
	{/snippet}
</Modal>
